import { useState } from "react";
import { Brain, Activity, Dumbbell, Check } from "lucide-react";
import { motion } from "framer-motion";
import { detectMuscleGroup } from "@/components/ExerciseMedia";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const TEST_SCENARIOS = [
  {
    name: "Dor Lombar vs Agachamento",
    painAreas: ["Lombar"],
    exercise: { exercise_name: "Agachamento Livre", library_data: { primary_muscle_group: "quadríceps" } }
  },
  {
    name: "Dor no Ombro vs Supino",
    painAreas: ["Ombro"],
    exercise: { exercise_name: "Supino Reto", library_data: { primary_muscle_group: "peito" } }
  },
  {
    name: "Dor no Joelho vs Leg Press",
    painAreas: ["Joelho"],
    exercise: { exercise_name: "Leg Press 45", library_data: { primary_muscle_group: "quadríceps" } }
  },
  {
    name: "Sem conflito: Dor Pulso vs Corrida",
    painAreas: ["Pulso"],
    exercise: { exercise_name: "Corrida na Esteira", library_data: { primary_muscle_group: "cardio" } }
  },
  {
    name: "Multi-conflito: Dor Lombar e Quadril vs Terra",
    painAreas: ["Lombar", "Quadril"],
    exercise: { exercise_name: "Levantamento Terra", library_data: { primary_muscle_group: "posterior_coxa" } }
  }
];

export default function JournalMappingTest() {
  const [activeScenario, setActiveScenario] = useState(TEST_SCENARIOS[0]);

  const renderWarning = (painAreas, currentEx) => {
    if (!currentEx || painAreas.length === 0) return null;
    
    const muscle = currentEx.library_data?.primary_muscle_group || detectMuscleGroup(currentEx.exercise_name).emoji;
    const muscleLower = String(muscle).toLowerCase();
    
    const affectedAreas = painAreas.filter(area => {
      const areaLower = area.toLowerCase();
      if (areaLower.includes("lombar") && (muscleLower.includes("costas") || muscleLower.includes("posterior") || muscleLower.includes("glúteo"))) return true;
      if (areaLower.includes("ombro") && (muscleLower.includes("ombro") || muscleLower.includes("peito"))) return true;
      if (areaLower.includes("joelho") && (muscleLower.includes("quadríceps") || muscleLower.includes("posterior") || muscleLower.includes("glúteo"))) return true;
      if (areaLower.includes("cervical") && (muscleLower.includes("trapézio") || muscleLower.includes("ombro"))) return true;
      if (areaLower.includes("pulso") && (muscleLower.includes("antebraço") || muscleLower.includes("bíceps") || muscleLower.includes("tríceps"))) return true;
      if (areaLower.includes("quadril") && (muscleLower.includes("glúteo") || muscleLower.includes("adutores") || muscleLower.includes("abdutores"))) return true;
      return muscleLower.includes(areaLower);
    });

    if (affectedAreas.length === 0) return (
      <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-green-500 text-sm flex items-center gap-2">
        <Check className="h-4 w-4" /> Sem conflitos detectados para este exercício.
      </div>
    );

    return (
      <motion.div 
        initial={{ opacity: 0, y: -10 }} 
        animate={{ opacity: 1, y: 0 }}
        className="w-full p-3 rounded-2xl bg-destructive/10 border border-destructive/20 flex flex-col gap-2"
      >
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-xl bg-destructive/20 flex items-center justify-center shrink-0">
            <Activity className="h-4 w-4 text-destructive" />
          </div>
          <div>
            <p className="text-xs font-bold text-destructive">Atenção ao corpo!</p>
            <p className="text-[10px] text-destructive/80">Você relatou desconforto em: {painAreas.join(", ")}.</p>
          </div>
        </div>
        
        <div className="bg-destructive/10 rounded-xl p-2.5 mt-1 border border-destructive/10">
          <p className="text-[10px] font-bold text-destructive flex items-center gap-1.5">
            <Brain className="h-3 w-3" /> Sugestão de ajuste:
          </p>
          <p className="text-[10px] text-destructive/90 mt-0.5 leading-relaxed">
            Este exercício trabalha {affectedAreas.join(" e ")}, área(s) que você relatou dor. 
            <span className="font-bold"> Reduza a carga em 25-30%</span> ou troque por uma variação mais confortável.
          </p>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-background p-6 space-y-8 max-w-2xl mx-auto">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">🧪 Teste de Mapeamento: Diário vs Treino</h1>
        <p className="text-muted-foreground text-sm">Valide se a lógica de detecção de dor está alertando corretamente os exercícios afetados.</p>
      </div>

      <div className="grid gap-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Cenários de Teste</h2>
        <div className="flex flex-wrap gap-2">
          {TEST_SCENARIOS.map((scenario) => (
            <Button 
              key={scenario.name}
              variant={activeScenario.name === scenario.name ? "default" : "outline"}
              onClick={() => setActiveScenario(scenario)}
              className="rounded-xl text-xs"
            >
              {scenario.name}
            </Button>
          ))}
        </div>
      </div>

      <Card className="p-6 space-y-6 bg-card/50 backdrop-blur-sm border-border/40">
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-primary font-semibold">
            <Dumbbell className="h-5 w-5" />
            <span>Exercício Ativo: {activeScenario.exercise.exercise_name}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Activity className="h-4 w-4" />
            <span>Dores relatadas no Diário: {activeScenario.painAreas.join(", ")}</span>
          </div>
        </div>

        <div className="pt-4 border-t border-border/40">
          <p className="text-xs font-bold text-muted-foreground uppercase mb-4 tracking-widest">Resultado do Componente</p>
          {renderWarning(activeScenario.painAreas, activeScenario.exercise)}
        </div>
      </Card>

      <div className="p-4 rounded-xl bg-muted/30 border border-border/50 text-xs text-muted-foreground space-y-2">
        <p className="font-bold">Lógica de Mapeamento:</p>
        <ul className="list-disc pl-4 space-y-1">
          <li>Lombar ↔ Costas, Posterior, Glúteo</li>
          <li>Ombro ↔ Ombro, Peito</li>
          <li>Joelho ↔ Quadríceps, Posterior, Glúteo</li>
          <li>Cervical ↔ Trapézio, Ombro</li>
          <li>Pulso ↔ Antebraço, Bíceps, Tríceps</li>
          <li>Quadril ↔ Glúteo, Adutores, Abdutores</li>
        </ul>
      </div>
    </div>
  );
}
